import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Palette, Mail, Save, RefreshCw, Send,
  ShoppingCart, Users, Server, FileSpreadsheet, Workflow as WorkflowIcon,
  CheckCircle2, XCircle, Plug, ShieldCheck, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import WorkflowTableEditor from "@/components/WorkflowTableEditor";
import { NEON, sectionCardStyle, sectionIconStyle } from "@/lib/statusColors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Each settings section owns one neon hue from the shared palette so the whole
// admin reads as one cohesive system.
const TAB_HUE = {
  branding:     NEON.blue,
  workflow:     NEON.cyan,
  integrations: NEON.purple,
  email:        NEON.amber,
  users:        NEON.green,
};

// Cohesive neon section card — left/top accent stripe, tinted icon chip, colored title.
const SectionCard = ({ hue, icon: Icon, title, description, actions, children }) => (
  <Card style={sectionCardStyle(hue)} className="border border-slate-100 overflow-hidden">
    <CardHeader className="flex flex-row items-start gap-3 space-y-0">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={sectionIconStyle(hue)}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <CardTitle className="text-lg" style={{ color: hue }}>{title}</CardTitle>
        {description && <CardDescription className="mt-1">{description}</CardDescription>}
      </div>
      {actions}
    </CardHeader>
    <CardContent className="space-y-4">{children}</CardContent>
  </Card>
);

const Settings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("branding");

  // Tenant info
  const [tenantName, setTenantName] = useState("");

  // Branding settings
  const [brandingSettings, setBrandingSettings] = useState({
    logo_url: "",
    primary_color: "#2196F3",
    secondary_color: "#9C27B0",
    font_family: "Arial, sans-serif",
    font_size_base: "16px"
  });

  // Email settings
  const [emailSettings, setEmailSettings] = useState({
    email_templates_enabled: true,
    bcc_email: "",
    manufacturer_can_change_status: false,
    manufacturer_can_add_notes: true,
    notes_visible_to_customer: false,
    manufacturer_can_email_customers: false,
    manufacturer_can_upload_tracking: true,
    order_manager_can_upload_tracking: true
  });

  // SMTP settings (kept fully functional — the Gmail app password is entered here)
  const [smtpSettings, setSmtpSettings] = useState({
    smtp_host: "",
    smtp_port: "587",
    smtp_user: "",
    smtp_password: "",
    smtp_from_email: ""
  });

  // Shopify is connected via the client-credentials token manager (SHOPIFYPF_* env,
  // "ProofingFlow July-2026" app) — no per-tenant credentials. We only display health.
  const [shopifyStatus, setShopifyStatus] = useState(null); // null = not yet checked
  const [checkingShopify, setCheckingShopify] = useState(false);
  const [syncingShopify, setSyncingShopify] = useState(false);
  const [syncingTags, setSyncingTags] = useState(false);
  const [fixingStages, setFixingStages] = useState(false);

  // Dashboard preference
  const [defaultDashboard, setDefaultDashboard] = useState("classic");

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    fetchSettings();
    fetchShopifyStatus();
  }, [navigate]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/tenant`);
      const settings = response.data.settings;

      setTenantName(response.data.name || "");

      setBrandingSettings({
        logo_url: settings.logo_url || "",
        primary_color: settings.primary_color || "#2196F3",
        secondary_color: settings.secondary_color || "#9C27B0",
        font_family: settings.font_family || "Arial, sans-serif",
        font_size_base: settings.font_size_base || "16px"
      });

      setEmailSettings({
        email_templates_enabled: settings.email_templates_enabled ?? true,
        bcc_email: settings.bcc_email || "",
        manufacturer_can_change_status: settings.manufacturer_can_change_status ?? false,
        manufacturer_can_add_notes: settings.manufacturer_can_add_notes ?? true,
        notes_visible_to_customer: settings.notes_visible_to_customer ?? false,
        manufacturer_can_email_customers: settings.manufacturer_can_email_customers ?? false,
        manufacturer_can_upload_tracking: settings.manufacturer_can_upload_tracking ?? true,
        order_manager_can_upload_tracking: settings.order_manager_can_upload_tracking ?? true
      });

      setSmtpSettings({
        smtp_host: settings.smtp_host || "",
        smtp_port: settings.smtp_port || "587",
        smtp_user: settings.smtp_user || "",
        smtp_password: "", // Don't load password for security
        smtp_from_email: settings.smtp_from_email || ""
      });

      setDefaultDashboard(settings.default_dashboard || "classic");
    } catch (error) {
      toast.error("Failed to load settings");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchShopifyStatus = async () => {
    setCheckingShopify(true);
    try {
      const response = await axios.get(`${API}/settings/shopify-status`);
      setShopifyStatus(response.data);
    } catch (error) {
      setShopifyStatus({ connected: false, detail: error.response?.data?.detail || "Unable to reach connection check." });
    } finally {
      setCheckingShopify(false);
    }
  };

  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/tenant`, {
        name: tenantName,
        settings: { ...brandingSettings, default_dashboard: defaultDashboard }
      });
      toast.success("Company branding saved successfully!");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Your session has expired. Please login again.");
        localStorage.removeItem('admin_token');
        setTimeout(() => navigate('/admin/login'), 2000);
      } else {
        toast.error(error.response?.data?.detail || "Failed to save branding settings");
      }
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEmail = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/tenant`, { settings: emailSettings });
      toast.success("Email & Permission settings saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save email settings");
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async () => {
    setSaving(true);
    try {
      // Only send the password when the admin actually typed a new one, so a blank
      // field never wipes the stored value.
      const payload = { ...smtpSettings };
      if (!smtpSettings.smtp_password) delete payload.smtp_password;
      await axios.patch(`${API}/settings/tenant`, { settings: payload });
      toast.success("SMTP settings saved successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save SMTP settings");
      console.error("Save error:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    const testEmail = prompt("Enter email address to send test email:");
    if (!testEmail) return;
    try {
      await axios.post(`${API}/settings/test-email`, { to_email: testEmail });
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
      console.error(error);
    }
  };

  const handleShopifySync = async () => {
    setSyncingShopify(true);
    try {
      const response = await axios.post(`${API}/settings/shopify/sync`, null, { params: { limit: 50 } });
      const result = response.data;
      toast.success(result.message || `Synced ${result.synced} orders from Shopify!`);
      if (result.errors && result.errors.length > 0) {
        console.warn("Shopify sync errors:", result.errors);
        toast.warning("Some orders had errors. Check console for details.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync Shopify orders");
      console.error("Shopify sync error:", error);
    } finally {
      setSyncingShopify(false);
    }
  };

  const handleBulkTagSync = async () => {
    setSyncingTags(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/orders/bulk-sync-shopify-tags`,
        { all_orders: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      const result = response.data;
      if (result.status === 'processing') {
        toast.success(`Started syncing ${result.total} orders in background. This may take a few minutes.`);
      } else {
        toast.success(`Synced ${result.success} order tags to Shopify! (${result.failed} failed)`);
      }
    } catch (error) {
      if (error.code === 'ECONNABORTED') {
        toast.info("Sync is running in the background. Check Shopify to verify.");
      } else {
        toast.error(error.response?.data?.detail || "Failed to sync tags");
      }
      console.error("Bulk tag sync error:", error);
    } finally {
      setSyncingTags(false);
    }
  };

  const handleFixOrderStages = async () => {
    setFixingStages(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API}/admin/orders/fix-stages`,
        { dry_run: false },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 }
      );
      const { results } = response.data;
      toast.success(
        `Fixed ${results.fulfilled_to_shipped.fixed} "Fulfilled" orders and ${results.tracking_workflow_applied.fixed} orders with tracking!`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix order stages");
      console.error("Fix stages error:", error);
    } finally {
      setFixingStages(false);
    }
  };

  const handleInitializeWorkflow = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API}/admin/workflow/initialize-config`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (result.success) {
        toast.success(`Workflow initialized: ${result.stages_created} stages, ${result.rules_created} rules created`);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(result.detail || "Failed to initialize workflow");
      }
    } catch (error) {
      toast.error("Failed to initialize workflow configuration");
      console.error(error);
    }
  };

  const shopifyConnected = shopifyStatus?.connected === true;

  const TABS = [
    { value: "branding",     label: "Branding",     icon: Palette },
    { value: "workflow",     label: "Workflow",     icon: WorkflowIcon },
    { value: "integrations", label: "Integrations", icon: Plug },
    { value: "email",        label: "Email",        icon: Mail },
    { value: "users",        label: "Users",        icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1
            className="text-4xl font-extrabold bg-clip-text text-transparent"
            style={{ backgroundImage: `linear-gradient(90deg, ${NEON.blue}, ${NEON.purple}, ${NEON.green})` }}
          >
            Settings
          </h1>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 h-auto p-1 bg-slate-100/80">
            {TABS.map(({ value, label, icon: Icon }) => {
              const active = activeTab === value;
              return (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="flex items-center gap-2 py-2 font-semibold transition-all"
                  style={active ? { color: TAB_HUE[value], boxShadow: `inset 0 -2px 0 ${TAB_HUE[value]}` } : undefined}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{label}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* ── Branding ────────────────────────────────────────────── */}
          <TabsContent value="branding" className="space-y-4">
            <SectionCard hue={TAB_HUE.branding} icon={Palette} title="Company Branding"
              description="Customize your company name, logo, colors, and typography">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name *</Label>
                <Input id="company-name" placeholder="AllBobbleheads" value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)} />
                <p className="text-sm text-gray-500">Your company name used in emails and customer portal</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="logo-url">Logo URL</Label>
                <Input id="logo-url" placeholder="https://example.com/logo.png" value={brandingSettings.logo_url}
                  onChange={(e) => setBrandingSettings({ ...brandingSettings, logo_url: e.target.value })} />
                <p className="text-sm text-gray-500">Displayed in emails and the customer portal (recommended: 200×60px)</p>
                {brandingSettings.logo_url && (
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600 mb-2">Preview:</p>
                    <img src={brandingSettings.logo_url} alt="Logo preview" className="max-h-16"
                      onError={(e) => e.target.style.display = 'none'} />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary-color">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input id="primary-color" type="color" value={brandingSettings.primary_color}
                      onChange={(e) => setBrandingSettings({ ...brandingSettings, primary_color: e.target.value })} className="w-20 h-10" />
                    <Input value={brandingSettings.primary_color} placeholder="#2196F3"
                      onChange={(e) => setBrandingSettings({ ...brandingSettings, primary_color: e.target.value })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary-color">Secondary Color</Label>
                  <div className="flex gap-2">
                    <Input id="secondary-color" type="color" value={brandingSettings.secondary_color}
                      onChange={(e) => setBrandingSettings({ ...brandingSettings, secondary_color: e.target.value })} className="w-20 h-10" />
                    <Input value={brandingSettings.secondary_color} placeholder="#9C27B0"
                      onChange={(e) => setBrandingSettings({ ...brandingSettings, secondary_color: e.target.value })} />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="font-family">Font Family</Label>
                  <Input id="font-family" value={brandingSettings.font_family} placeholder="Arial, sans-serif"
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, font_family: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="font-size">Base Font Size</Label>
                  <Input id="font-size" value={brandingSettings.font_size_base} placeholder="16px"
                    onChange={(e) => setBrandingSettings({ ...brandingSettings, font_size_base: e.target.value })} />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="default-dashboard">Default Dashboard View</Label>
                <select id="default-dashboard" className="w-full p-2 border rounded-md bg-white" value={defaultDashboard}
                  onChange={(e) => setDefaultDashboard(e.target.value)}>
                  <option value="classic">Classic Dashboard</option>
                  <option value="orderdesk">OrderDesk View</option>
                </select>
                <p className="text-sm text-gray-500">Which dashboard view to show by default when logging in</p>
              </div>

              <Button onClick={handleSaveBranding} disabled={saving} className="w-full text-white"
                style={{ backgroundColor: TAB_HUE.branding }}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Branding Settings"}
              </Button>
            </SectionCard>
          </TabsContent>

          {/* ── Workflow ────────────────────────────────────────────── */}
          <TabsContent value="workflow" className="space-y-4">
            <SectionCard hue={TAB_HUE.workflow} icon={WorkflowIcon} title="Workflow Stages & Rules"
              description="Define your stages, statuses, and transition rules">
              <WorkflowTableEditor />
            </SectionCard>

            <SectionCard hue={TAB_HUE.workflow} icon={RefreshCw} title="Initialize Workflow Configuration"
              description="Set up (or repair) stage definitions and default rules — existing settings are preserved">
              <Button onClick={handleInitializeWorkflow} variant="outline" data-testid="initialize-workflow-btn"
                className="w-full" style={{ borderColor: TAB_HUE.workflow, color: TAB_HUE.workflow }}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Initialize Workflow Configuration
              </Button>
            </SectionCard>
          </TabsContent>

          {/* ── Integrations ────────────────────────────────────────── */}
          <TabsContent value="integrations" className="space-y-4">
            {/* Shopify — read-only connection status (token manager, no stored creds) */}
            <SectionCard hue={TAB_HUE.integrations} icon={ShoppingCart} title="Shopify"
              description="Order & tracking sync — connection is auto-managed, no keys to enter"
              actions={
                <Button variant="ghost" size="sm" onClick={fetchShopifyStatus} disabled={checkingShopify}>
                  <RefreshCw className={`w-4 h-4 ${checkingShopify ? 'animate-spin' : ''}`} />
                </Button>
              }>
              {/* Connection status card */}
              {shopifyStatus === null ? (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-slate-50 border border-slate-200">
                  <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
                  <span className="text-sm text-slate-500">Checking connection…</span>
                </div>
              ) : shopifyConnected ? (
                <div className="flex items-start gap-3 p-4 rounded-xl border"
                  style={{ backgroundColor: `${NEON.green}12`, borderColor: `${NEON.green}55` }}>
                  <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: NEON.green }} />
                  <div className="min-w-0">
                    <p className="font-bold" style={{ color: NEON.green }}>
                      Shopify: Connected ✓
                    </p>
                    <p className="text-sm text-slate-600">
                      Auto-managed via <strong>{shopifyStatus.app || "ProofingFlow July-2026"}</strong> app
                      {shopifyStatus.shop_domain ? ` — ${shopifyStatus.shop_domain}` : ""}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 p-4 rounded-xl border"
                  style={{ backgroundColor: `${NEON.red}12`, borderColor: `${NEON.red}55` }}>
                  <XCircle className="w-6 h-6 shrink-0" style={{ color: NEON.red }} />
                  <div className="min-w-0">
                    <p className="font-bold" style={{ color: NEON.red }}>Shopify: Not Connected</p>
                    <p className="text-sm text-slate-600 break-words">
                      {shopifyStatus.detail || "The token manager could not mint an access token."}
                    </p>
                  </div>
                </div>
              )}

              {/* Sync actions — gated on a live connection */}
              <div className="space-y-2 pt-1">
                <Button onClick={handleShopifySync} disabled={syncingShopify || !shopifyConnected}
                  variant="outline" className="w-full">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingShopify ? 'animate-spin' : ''}`} />
                  {syncingShopify ? "Syncing..." : "Sync Orders from Shopify"}
                </Button>
                <Button onClick={handleBulkTagSync} disabled={syncingTags || !shopifyConnected}
                  variant="outline" className="w-full">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingTags ? 'animate-spin' : ''}`} />
                  {syncingTags ? "Syncing Tags..." : "Sync All Order Tags to Shopify"}
                </Button>
                <Button onClick={handleFixOrderStages} disabled={fixingStages} variant="outline"
                  className="w-full border-orange-300 text-orange-700 hover:bg-orange-50" data-testid="fix-order-stages-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${fixingStages ? 'animate-spin' : ''}`} />
                  {fixingStages ? "Fixing Stages..." : "Fix Order Stages (Fulfilled → Shipped)"}
                </Button>
                <p className="text-xs text-gray-500 text-center">
                  Converts "Fulfilled" orders to "Shipped" and applies workflow rules to orders with tracking
                </p>
              </div>
            </SectionCard>

            {/* SMTP — fully functional; Gmail app password entered here */}
            <SectionCard hue={TAB_HUE.integrations} icon={Server} title="SMTP / Email Server"
              description="Outbound mail server for automated customer notifications">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp-host">SMTP Host *</Label>
                  <Input id="smtp-host" type="text" placeholder="smtp.gmail.com" value={smtpSettings.smtp_host}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_host: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp-port">SMTP Port *</Label>
                  <Input id="smtp-port" type="text" placeholder="587" value={smtpSettings.smtp_port}
                    onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_port: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-user">SMTP Username *</Label>
                <Input id="smtp-user" type="text" placeholder="your-email@gmail.com" value={smtpSettings.smtp_user}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_user: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-password">SMTP Password *</Label>
                <Input id="smtp-password" type="password" placeholder="••••••••" value={smtpSettings.smtp_password}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_password: e.target.value })} />
                <p className="text-xs text-gray-500">Leave blank to keep the existing password. For Gmail, use a 16-character App Password.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtp-from">From Email Address *</Label>
                <Input id="smtp-from" type="email" placeholder="noreply@yourcompany.com" value={smtpSettings.smtp_from_email}
                  onChange={(e) => setSmtpSettings({ ...smtpSettings, smtp_from_email: e.target.value })} />
                <p className="text-xs text-gray-500">Address that appears as the sender</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 pt-1">
                <Button onClick={handleSaveSmtp} disabled={saving} className="flex-1 text-white"
                  style={{ backgroundColor: TAB_HUE.integrations }}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save SMTP Settings"}
                </Button>
                <Button onClick={handleSendTestEmail} variant="outline" className="flex-1">
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Email
                </Button>
              </div>
            </SectionCard>

            {/* Google Sheets */}
            <SectionCard hue={TAB_HUE.integrations} icon={FileSpreadsheet} title="Google Sheets Logging"
              description="Automatically log order activity to a Google Spreadsheet">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg text-center">
                <p className="text-sm text-slate-600 mb-3">
                  Connect your Google account and choose a spreadsheet for logging.
                </p>
                <Button variant="outline" size="sm"
                  onClick={() => window.location.href = `${API}/oauth/sheets/login`}
                  style={{ borderColor: TAB_HUE.integrations, color: TAB_HUE.integrations }}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Configure Google Sheets
                </Button>
                <p className="text-xs text-gray-500 mt-2">You'll be redirected to Google to authorize access</p>
              </div>
            </SectionCard>
          </TabsContent>

          {/* ── Email ───────────────────────────────────────────────── */}
          <TabsContent value="email" className="space-y-4">
            <SectionCard hue={TAB_HUE.email} icon={Mail} title="Email Notifications"
              description="Manage email templates and notification settings">
              <div className="p-4 rounded-lg border" style={{ backgroundColor: `${NEON.amber}12`, borderColor: `${NEON.amber}44` }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#8a5a00' }}>Email Templates</p>
                <p className="text-sm text-gray-600 mb-3">
                  Customize the content and appearance of automated emails sent to customers
                </p>
                <Button onClick={() => navigate('/admin/email-templates')} variant="outline" size="sm"
                  style={{ borderColor: TAB_HUE.email, color: '#8a5a00' }}>
                  <Mail className="w-4 h-4 mr-2" />
                  Manage Email Templates
                </Button>
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Enable Email Notifications</Label>
                  <p className="text-sm text-gray-500">Turn on/off all automatic customer email notifications</p>
                </div>
                <Switch checked={emailSettings.email_templates_enabled}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, email_templates_enabled: checked })} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bcc-email">BCC Email Address</Label>
                <Input id="bcc-email" type="email" placeholder="admin@example.com" value={emailSettings.bcc_email}
                  onChange={(e) => setEmailSettings({ ...emailSettings, bcc_email: e.target.value })} />
                <p className="text-sm text-gray-500">Receive a blind copy of all outgoing customer emails</p>
              </div>

              <Button onClick={handleSaveEmail} disabled={saving} className="w-full text-white"
                style={{ backgroundColor: TAB_HUE.email }}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Email Settings"}
              </Button>
            </SectionCard>
          </TabsContent>

          {/* ── Users ───────────────────────────────────────────────── */}
          <TabsContent value="users" className="space-y-4">
            <SectionCard hue={TAB_HUE.users} icon={Users} title="User Management"
              description="Add, edit, and manage team member accounts">
              <Button onClick={() => navigate('/admin/users')} variant="outline"
                className="w-full" style={{ borderColor: TAB_HUE.users, color: '#0a7a48' }}>
                <Users className="w-4 h-4 mr-2" />
                Manage Users
              </Button>
            </SectionCard>

            <SectionCard hue={TAB_HUE.users} icon={ShieldCheck} title="Role Permissions"
              description="Control what manufacturers and order managers can do">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label>Can Email Customers Directly</Label>
                  <p className="text-sm text-gray-500">Allow manufacturers to email customers about their orders</p>
                </div>
                <Switch checked={emailSettings.manufacturer_can_email_customers}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, manufacturer_can_email_customers: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Can Change Order Status</Label>
                  <p className="text-sm text-gray-500">Allow manufacturers to update order statuses</p>
                </div>
                <Switch checked={emailSettings.manufacturer_can_change_status}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, manufacturer_can_change_status: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Can Add Notes</Label>
                  <p className="text-sm text-gray-500">Allow manufacturers to add internal notes to orders</p>
                </div>
                <Switch checked={emailSettings.manufacturer_can_add_notes}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, manufacturer_can_add_notes: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Notes Visible to Customers</Label>
                  <p className="text-sm text-gray-500">Show manufacturer and admin notes to customers in their portal</p>
                </div>
                <Switch checked={emailSettings.notes_visible_to_customer}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, notes_visible_to_customer: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Manufacturer Can Upload Tracking</Label>
                  <p className="text-sm text-gray-500">Allow manufacturer role to add tracking information</p>
                </div>
                <Switch checked={emailSettings.manufacturer_can_upload_tracking}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, manufacturer_can_upload_tracking: checked })} />
              </div>
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Order Manager Can Upload Tracking</Label>
                  <p className="text-sm text-gray-500">Allow customer service role to add tracking information</p>
                </div>
                <Switch checked={emailSettings.order_manager_can_upload_tracking}
                  onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, order_manager_can_upload_tracking: checked })} />
              </div>

              <Button onClick={handleSaveEmail} disabled={saving} className="w-full text-white"
                style={{ backgroundColor: TAB_HUE.users }}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Permission Settings"}
              </Button>
            </SectionCard>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Settings;
